import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, useModal } from "../../modal";
import {
  Button,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  GridContainer,
  GridItem,
  DataCard,
  DataCardHeader,
  DataCardTitle,
  DataCardContent,
  DataItem,
  DataLabel,
  DataValue,
  AmountValue,
  ISOHeader,
  ISOHeaderTitle,
  HoldingsTableContainer,
  HoldingsTable,
  HoldingsTableHeader,
  HoldingsCollapseContent,
} from "./MerchantPeriodicReport.styles";
import { ResetStore } from "./MerchantPeriodicReport.slice";
import { formatUtcToLocal, getLocalTzLabel } from "../../../services/dateUtils";
import {
  AccountBalance,
  Receipt,
  AttachMoney,
  Groups,
  Store,
  DevicesOther,
  ListAlt,
} from "@mui/icons-material";

const TABLE_COLUMNS = [
  "Amount",
  "Transactions",
  "MDR Fee",
  "Processing Fee",
  "Settlement",
];

const TRANSACTION_DETAIL_COLUMNS = [
  "Transaction ID",
  "Crypto exchange",
  "Tx Amount",
  "Settlement Amount",
];

const formatDecimal = (value) => {
  if (typeof value === "string") {
    const cleanedValue = value.replace(",", ".").replace(/[^0-9.]/g, "");
    if ((cleanedValue.match(/\./g) || []).length > 1) return "0.0000";
    const number = parseFloat(cleanedValue);
    if (!isNaN(number)) return number.toFixed(4);
  }
  if (typeof value === "number") return value.toFixed(4);
  return "0.0000";
};

const formatDate = (rawDate) => {
  const s = rawDate.toString();
  const year = s.slice(0, 4);
  const month = s.slice(4, 6);
  const day = s.slice(6, 8);
  return `${month}/${day}/${year}`;
};

const tzLabel = getLocalTzLabel();

// executed_at viene en UTC; el CSV usa la misma hora local que la UI.
const formatDateTime = (executedAt) =>
  formatUtcToLocal(executedAt, { second: "2-digit" });

const formatNumber = (value) => {
  if (value == null) return "0.00";
  const num = parseFloat(value);
  return Number.isNaN(num)
    ? "0.00"
    : num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const formatTransactionAmountForCsv = (value) => {
  if (value == null) return "";
  const num = parseFloat(value);
  return Number.isNaN(num) ? String(value) : num.toFixed(2);
};

const trimStr = (v) => {
  if (v == null) return "";
  const s = String(v).trim();
  return s === "" ? "" : s;
};

/**
 * API puede mandar nombre en camelCase, snake_case o dentro de `crypto`.
 * Si la tx no trae exchange, se usa el del terminal (misma agrupación que los rollups del terminal).
 */
const getTransactionExchangeDisplay = (tx, terminal) => {
  const fromTx =
    trimStr(tx?.exchangeName) ||
    trimStr(tx?.exchange_name) ||
    trimStr(tx?.crypto?.exchangeName) ||
    trimStr(tx?.crypto?.exchange_name);
  if (fromTx) return fromTx;

  const fromTxId =
    trimStr(tx?.exchangeID) ||
    trimStr(tx?.exchange_id) ||
    trimStr(tx?.crypto?.exchangeID) ||
    trimStr(tx?.crypto?.exchange_id);
  if (fromTxId) return fromTxId;

  const fromTerminal =
    trimStr(terminal?.exchangeName) ||
    trimStr(terminal?.exchange_name) ||
    trimStr(terminal?.exchangeID) ||
    trimStr(terminal?.exchange_id);
  if (fromTerminal) return fromTerminal;

  return "N/A";
};

const normalizeTransactionList = (transactions) => {
  if (transactions == null) return [];
  return Array.isArray(transactions) ? transactions : [transactions];
};

const isPositiveAmount = (value) => {
  const num = parseFloat(value);
  return !Number.isNaN(num) && num >= 0;
};

const AmountCell = ({ value, currency }) => {
  const theme = useTheme();
  const num = parseFloat(value);
  const positive = isPositiveAmount(value);
  const color = Number.isNaN(num)
    ? "inherit"
    : positive
      ? theme.palette.success.main
      : theme.palette.error.main;
  const prefix = currency === "USD" ? "$" : "";
  return (
    <TableCell align="center" sx={{ color, fontWeight: 600 }}>
      {prefix} {Number.isNaN(num) ? value : num.toFixed(2)}
    </TableCell>
  );
};

const MerchantPeriodicReportResultModal = () => {
  const dispatch = useDispatch();
  const { close } = useModal();
  const { isOpen } = useSelector((state) => state.modal);
  const {
    consolidatedData,
    merchantData,
    merchantHolding,
    currency: storedCurrency,
  } = useSelector((state) => state.merchantPeriodicReport);
  const currency = storedCurrency || "USD";

  if (!isOpen) return null;

  const handleClose = () => {
    close();
    dispatch(ResetStore());
  };

  const downloadCSV = () => {
    const headers = [
      "Date",
      "Holding Name",
      "Holding Settlement",
      "Holding Amount",
      "Holding Trans. Count",
      "Group Name",
      "Group Settlement",
      "Group Amount",
      "Group Transactions",
      "Group MDR Fee",
      "Group Proc. Fee",
      "Merchant Name",
      "MID",
      "Merchant Settlement",
      "Merchant Amount",
      "Merchant Trans. Count",
      "Terminal ID",
      "Terminal Crypto exchange",
      "Terminal Settlement",
      "Terminal Amount",
      "Terminal Trans. Count",
      "Terminal MDR Fee",
      "Terminal Proc. Fee",
    ];

    const rows = [];
    if (!merchantData || !Array.isArray(merchantData)) {
      const csvContent = [headers].map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "date_range_report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    merchantData.forEach((entry) => {
      const date = formatDate(entry.date);
      const holdings = entry.holdings;
      const holdingAmountDue = formatDecimal(holdings.amountDue);
      const holdingTxAmount = formatDecimal(holdings.transactionsAmount);
      const holdingTxCount = holdings.transactionsCount;

      if (holdings.groups && holdings.groups.group) {
        holdings.groups.group.forEach((group) => {
          if (group.merchants && group.merchants.merchant) {
            group.merchants.merchant.forEach((merchant) => {
              if (merchant.terminals && merchant.terminals.terminal) {
                merchant.terminals.terminal.forEach((terminal) => {
                  rows.push([
                    date,
                    merchantHolding || "",
                    holdingAmountDue,
                    holdingTxAmount,
                    holdingTxCount,
                    group.merchantGroup || "",
                    formatDecimal(group.amountDue),
                    formatDecimal(group.transactionsAmount),
                    group.transactionsCount,
                    formatDecimal(group.txMDR),
                    formatDecimal(group.txProcessing),
                    merchant.name || "",
                    merchant.ID || "",
                    formatDecimal(merchant.amountDue),
                    formatDecimal(merchant.transactionsAmount),
                    merchant.transactionsCount,
                    terminal.terminalID || "",
                    getTransactionExchangeDisplay({}, terminal),
                    formatDecimal(terminal.amountDue),
                    formatDecimal(terminal.transactionsAmount),
                    terminal.transactionsCount,
                    formatDecimal(terminal.txMDR),
                    formatDecimal(terminal.txProcessing),
                  ]);
                });
              } else {
                rows.push([
                  date,
                  group.merchantHolding || "",
                  holdingAmountDue,
                  holdingTxAmount,
                  holdingTxCount,
                  group.merchantGroup || "",
                  formatDecimal(group.amountDue),
                  formatDecimal(group.transactionsAmount),
                  group.transactionsCount,
                  formatDecimal(group.txMDR),
                  formatDecimal(group.txProcessing),
                  merchant.name || "",
                  merchant.ID || "",
                  formatDecimal(merchant.amountDue),
                  formatDecimal(merchant.transactionsAmount),
                  merchant.transactionsCount,
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                ]);
              }
            });
          } else {
            rows.push([
              date,
              group.merchantHolding || "",
              holdingAmountDue,
              holdingTxAmount,
              holdingTxCount,
              group.merchantGroup || "",
              formatDecimal(group.amountDue),
              formatDecimal(group.transactionsAmount),
              group.transactionsCount,
              formatDecimal(group.txMDR),
              formatDecimal(group.txProcessing),
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
            ]);
          }
        });
      } else {
        rows.push([
          date,
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
        ]);
      }
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "date_range_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadTransactionsCSV = () => {
    const headers = [
      "Terminal ID",
      "Transaction ID",
      "Tx Amount",
      "Settlement Amount",
      "Crypto exchange",
      tzLabel ? `Date & time (${tzLabel})` : "Date & time",
    ];
    const rows = [];
    if (!merchantData || !Array.isArray(merchantData)) {
      const csvContent = [headers]
        .map((row) => row.map((cell) => `"${cell}"`).join(","))
        .join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "merchant_transactions_report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    merchantData.forEach((entry) => {
      const holdings = entry.holdings;
      if (!holdings?.groups?.group) return;
      holdings.groups.group.forEach((group) => {
        if (!group.merchants?.merchant) return;
        group.merchants.merchant.forEach((merchant) => {
          if (!merchant.terminals?.terminal) return;
          merchant.terminals.terminal.forEach((terminal) => {
            const terminalID = terminal.terminalID ?? "";
            const transactions = normalizeTransactionList(terminal.transactions);
            if (transactions.length === 0) return;
            transactions.forEach((tx) => {
              rows.push([
                terminalID,
                tx.acquirer_transaction_id ?? "",
                formatTransactionAmountForCsv(tx.amount),
                formatTransactionAmountForCsv(tx.amountDue),
                getTransactionExchangeDisplay(tx, terminal),
                formatDateTime(tx.executed_at),
              ]);
            });
          });
        });
      });
    });
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "merchant_transactions_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderTerminalTransactions = (terminal) => {
    const transactions = normalizeTransactionList(terminal?.transactions);
    if (transactions.length === 0) {
      return null;
    }
    return (
      <DataCard sx={{ mt: 2, marginBottom: "15px" }}>
        <DataCardHeader>
          <ListAlt color="primary" />
          <DataCardTitle>Transactions</DataCardTitle>
        </DataCardHeader>
        <DataCardContent>
          <HoldingsTableContainer>
            <TableContainer component={Paper} elevation={0}>
              <HoldingsTable size="small">
                <HoldingsTableHeader>
                  <TableRow>
                    {TRANSACTION_DETAIL_COLUMNS.map((col) => (
                      <TableCell key={col} align="center">
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </HoldingsTableHeader>
                <TableBody>
                  {transactions.map((tx, txIdx) => (
                    <TableRow key={txIdx}>
                      <TableCell align="center">{tx.acquirer_transaction_id ?? ""}</TableCell>
                      <TableCell align="center">
                        {getTransactionExchangeDisplay(tx, terminal)}
                      </TableCell>
                      <AmountCell value={tx.amount} currency={currency} />
                      <AmountCell value={tx.amountDue} currency={currency} />
                    </TableRow>
                  ))}
                </TableBody>
              </HoldingsTable>
            </TableContainer>
          </HoldingsTableContainer>
        </DataCardContent>
      </DataCard>
    );
  };

  const renderTerminalsTable = (terminals) => (
    <DataCard sx={{ mt: 2, marginBottom: "15px" }}>
      <DataCardHeader>
        <DevicesOther color="primary" />
        <DataCardTitle>Terminals</DataCardTitle>
      </DataCardHeader>
      <DataCardContent>
        <HoldingsCollapseContent>
          {terminals.map((terminal, idx) => (
            <Accordion key={idx} sx={{ mb: 1 }} elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">
                  {terminal.terminalID} - {terminal.exchangeName || "N/A"}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <TableContainer component={Paper} elevation={0}>
                  <HoldingsTable size="small">
                    <HoldingsTableHeader>
                      <TableRow>
                        {TABLE_COLUMNS.map((col) => (
                          <TableCell key={col} align="center">{col}</TableCell>
                        ))}
                      </TableRow>
                    </HoldingsTableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell align="center">
                          {currency === "USD" ? "$" : ""} {formatNumber(terminal.transactionsAmount)}
                        </TableCell>
                        <TableCell align="center">{terminal.transactionsCount}</TableCell>
                        <TableCell align="center">
                          {currency === "USD" ? "$" : ""} {formatNumber(terminal.txMDR)}
                        </TableCell>
                        <TableCell align="center">
                          {currency === "USD" ? "$" : ""} {formatNumber(terminal.txProcessing)}
                        </TableCell>
                        <AmountCell value={terminal.amountDue} currency={currency} />
                      </TableRow>
                    </TableBody>
                  </HoldingsTable>
                </TableContainer>
                {renderTerminalTransactions(terminal)}
              </AccordionDetails>
            </Accordion>
          ))}
        </HoldingsCollapseContent>
      </DataCardContent>
    </DataCard>
  );

  const renderMerchantsTable = (merchants) => (
    <DataCard sx={{ mt: 2, marginBottom: "15px" }}>
      <DataCardHeader>
        <Store color="primary" />
        <DataCardTitle>Merchants</DataCardTitle>
      </DataCardHeader>
      <DataCardContent>
        <HoldingsCollapseContent>
          {merchants.map((merchant, idx) => (
            <Accordion key={idx} sx={{ mb: 1 }} elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">
                  {merchant.name} - {merchant.ID} - {merchant.store}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <TableContainer component={Paper} elevation={0}>
                  <HoldingsTable size="small">
                    <HoldingsTableHeader>
                      <TableRow>
                        {TABLE_COLUMNS.map((col) => (
                          <TableCell key={col} align="center">{col}</TableCell>
                        ))}
                      </TableRow>
                    </HoldingsTableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell align="center">
                          {currency === "USD" ? "$" : ""} {formatNumber(merchant.transactionsAmount)}
                        </TableCell>
                        <TableCell align="center">{merchant.transactionsCount}</TableCell>
                        <TableCell align="center">
                          {currency === "USD" ? "$" : ""} {formatNumber(merchant.txMDR)}
                        </TableCell>
                        <TableCell align="center">
                          {currency === "USD" ? "$" : ""} {formatNumber(merchant.txProcessing)}
                        </TableCell>
                        <AmountCell value={merchant.amountDue} currency={currency} />
                      </TableRow>
                    </TableBody>
                  </HoldingsTable>
                </TableContainer>
                {merchant.terminals &&
                  merchant.terminals.terminal &&
                  renderTerminalsTable(merchant.terminals.terminal)}
              </AccordionDetails>
            </Accordion>
          ))}
        </HoldingsCollapseContent>
      </DataCardContent>
    </DataCard>
  );

  const renderGroupsTable = (groups) => (
    <DataCard sx={{ mt: 2, marginBottom: "15px" }}>
      <DataCardHeader>
        <Groups color="primary" />
        <DataCardTitle>Groups</DataCardTitle>
      </DataCardHeader>
      <DataCardContent>
        <HoldingsCollapseContent>
          {groups.map((group, idx) => (
            <Accordion key={idx} sx={{ mb: 1 }} elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">
                  {group.merchantGroup} - {group.merchantGroupID}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <TableContainer component={Paper} elevation={0}>
                  <HoldingsTable size="small">
                    <HoldingsTableHeader>
                      <TableRow>
                        {TABLE_COLUMNS.map((col) => (
                          <TableCell key={col} align="center">{col}</TableCell>
                        ))}
                      </TableRow>
                    </HoldingsTableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell align="center">
                          {currency === "USD" ? "$" : ""} {formatNumber(group.transactionsAmount)}
                        </TableCell>
                        <TableCell align="center">{group.transactionsCount}</TableCell>
                        <TableCell align="center">
                          {currency === "USD" ? "$" : ""} {formatNumber(group.txMDR)}
                        </TableCell>
                        <TableCell align="center">
                          {currency === "USD" ? "$" : ""} {formatNumber(group.txProcessing)}
                        </TableCell>
                        <AmountCell value={group.amountDue} currency={currency} />
                      </TableRow>
                    </TableBody>
                  </HoldingsTable>
                </TableContainer>
                {group.merchants &&
                  group.merchants.merchant &&
                  renderMerchantsTable(group.merchants.merchant)}
              </AccordionDetails>
            </Accordion>
          ))}
        </HoldingsCollapseContent>
      </DataCardContent>
    </DataCard>
  );

  return (
    <Modal
      fullScreen
      actions={
        <>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
          <Button onClick={downloadTransactionsCSV} variant="outlined">
            Download transactions CSV
          </Button>
          <Button onClick={downloadCSV} variant="contained">
            Download CSV
          </Button>
        </>
      }
    >
      <GridContainer>
        <GridItem xs={12} margin="0 0 15px 0">
          <ISOHeader>
            <ISOHeaderTitle>Merchant Details</ISOHeaderTitle>
          </ISOHeader>
        </GridItem>

        {consolidatedData && (
          <>
            <GridItem xs={12} md={6} padding="15px" margin="0 0 15px 0">
              <DataCard>
                <DataCardHeader>
                  <Receipt color="primary" />
                  <DataCardTitle>Transaction Summary</DataCardTitle>
                </DataCardHeader>
                <DataCardContent>
                  <DataItem>
                    <DataLabel>Transaction Count</DataLabel>
                    <DataValue>{consolidatedData.transactionsCount || 0}</DataValue>
                  </DataItem>
                  <DataItem>
                    <DataLabel>Amount</DataLabel>
                    <DataValue>
                      {currency === "USD" ? "$" : ""}
                      {formatNumber(consolidatedData.transactionsAmount)}
                    </DataValue>
                  </DataItem>
                </DataCardContent>
              </DataCard>
            </GridItem>
            <GridItem xs={12} md={6} padding="15px" margin="0 0 15px 0">
              <DataCard>
                <DataCardHeader>
                  <AttachMoney color="primary" />
                  <DataCardTitle>Summary</DataCardTitle>
                </DataCardHeader>
                <DataCardContent>
                  <DataItem>
                    <DataLabel>Settlement</DataLabel>
                    <AmountValue positive={isPositiveAmount(consolidatedData.amountDue)}>
                      {currency === "USD" ? "$" : ""}
                      {formatNumber(consolidatedData.amountDue)}
                    </AmountValue>
                  </DataItem>
                  <DataItem>
                    <DataLabel>Processing Fee</DataLabel>
                    <DataValue>
                      {currency === "USD" ? "$" : ""}
                      {formatNumber(consolidatedData.txProcessing)}
                    </DataValue>
                  </DataItem>
                  <DataItem>
                    <DataLabel>MDR Fee</DataLabel>
                    <DataValue>
                      {currency === "USD" ? "$" : ""}
                      {formatNumber(consolidatedData.txMDR)}
                    </DataValue>
                  </DataItem>
                </DataCardContent>
              </DataCard>
            </GridItem>
          </>
        )}

        {merchantData &&
          merchantData.map((dailyReport, index) => (
            <GridItem xs={12} key={index} padding="15px" margin="0 0 15px 0">
              <DataCard>
                <DataCardHeader>
                  <AccountBalance color="primary" />
                  <DataCardTitle>
                    {merchantHolding} | Date:{" "}
                    {new Date(
                      parseInt(dailyReport.date.toString().slice(0, 4), 10),
                      parseInt(dailyReport.date.toString().slice(4, 6), 10) - 1,
                      parseInt(dailyReport.date.toString().slice(6, 8), 10),
                    ).toLocaleDateString("en-US")}
                  </DataCardTitle>
                </DataCardHeader>
                <DataCardContent>
                  <HoldingsTableContainer sx={{ mb: 2 }}>
                    <TableContainer component={Paper} elevation={0}>
                      <HoldingsTable size="small">
                        <HoldingsTableHeader>
                          <TableRow>
                            {TABLE_COLUMNS.map((col) => (
                              <TableCell key={col} align="center">
                                {col}
                              </TableCell>
                            ))}
                          </TableRow>
                        </HoldingsTableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell align="center">
                              {currency === "USD" ? "$" : ""}{" "}
                              {formatNumber(dailyReport.holdings?.transactionsAmount)}
                            </TableCell>
                            <TableCell align="center">
                              {dailyReport.holdings?.transactionsCount}
                            </TableCell>
                            <TableCell align="center">
                              {currency === "USD" ? "$" : ""}{" "}
                              {formatNumber(dailyReport.holdings?.txMDR)}
                            </TableCell>
                            <TableCell align="center">
                              {currency === "USD" ? "$" : ""}{" "}
                              {formatNumber(dailyReport.holdings?.txProcessing)}
                            </TableCell>
                            <AmountCell
                              value={dailyReport.holdings?.amountDue}
                              currency={currency}
                            />
                          </TableRow>
                        </TableBody>
                      </HoldingsTable>
                    </TableContainer>
                  </HoldingsTableContainer>
                  {dailyReport.holdings?.groups?.group &&
                    renderGroupsTable(dailyReport.holdings.groups.group)}
                </DataCardContent>
              </DataCard>
            </GridItem>
          ))}
      </GridContainer>
    </Modal>
  );
};

export default MerchantPeriodicReportResultModal;
